import { Box, IconButton, Tooltip } from '@mui/material';
import { MaterialReactTable, createRow, useMaterialReactTable } from 'material-react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

const emptyObject = { label: '', percent: { $numberDecimal: 0 } };

function DiscountsInput({ discounts = [], onChange }) {
    const [validationErrors, setValidationErrors] = useState({});
    const [curDiscounts, setCurDiscounts] = useState(discounts);

    useEffect(() => {
        onChange(curDiscounts);
    }, [curDiscounts, onChange]);

    const columns = useMemo(
        () => [
            {
                header: 'Label',
                accessorKey: 'label',
                size: 140,
                muiEditTextFieldProps: {
                    required: true,
                    error: !!validationErrors?.label,
                    helperText: validationErrors?.label,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            label: undefined,
                        }),
                },
            },
            {
                header: 'Percent',
                id: 'percent',
                accessorFn: (dataRow) => parseFloat(dataRow?.percent?.$numberDecimal),
                size: 130,
                muiEditTextFieldProps: {
                    type: 'number',
                    required: true,
                    error: !!validationErrors?.percent,
                    helperText: validationErrors?.percent,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            percent: undefined,
                        }),
                },
            },
        ],
        [validationErrors]
    );

    //CREATE action
    const handleCreateDiscount = useCallback(async ({ values, table }) => {
        const newValidationErrors = validateDiscount(values);
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }

        setValidationErrors({});
        setCurDiscounts((curDiscounts) => [
            {
                ...values,
                percent: { $numberDecimal: values.percent },
                tempId: (Math.random() + 1).toString(36).substring(7),
            },
            ...curDiscounts,
        ]);
        table.setCreatingRow(null);
    }, []);

    //UPDATE action
    const handleSaveDiscount = useCallback(async ({ values, row, table }) => {
        const newValidationErrors = validateDiscount(values);
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }

        setValidationErrors({});
        setCurDiscounts((curDiscounts) =>
            curDiscounts.map((type) =>
                type._id === row.id || type.tempId === row.id
                    ? {
                          ...values,
                          _id: row.original._id,
                          percent: {
                              $numberDecimal: values.percent.toString(),
                          },
                      }
                    : { ...type }
            )
        );
        table.setEditingRow(null);
    }, []);

    //DELETE action
    const openDeleteConfirmModal = useCallback((row) => {
        if (window.confirm('Are you sure you want to delete this discount?')) {
            setCurDiscounts((curDiscounts) => {
                return curDiscounts.filter((type) => type._id !== row.id && type.tempId !== row.id);
            });
        }
    }, []);

    const table = useMaterialReactTable({
        columns,

        data: curDiscounts,

        enableColumnOrdering: false,

        enableDensityToggle: false,

        enablePagination: false,

        enableEditing: true,

        enableRowActions: true,

        enableColumnFilters: false,

        enableSorting: false,

        enableColumnFilterModes: false,

        enableHiding: false,

        enableFullScreenToggle: false,

        enableColumnActions: false,

        enableBottomToolbar: false,

        positionActionsColumn: 'last',

        createDisplayMode: 'row',

        editDisplayMode: 'row',

        layoutMode: 'grid-no-grow',

        getRowId: (row) => (row.tempId ? row.tempId : row._id),

        onCreatingRowCancel: () => setValidationErrors({}),

        onCreatingRowSave: handleCreateDiscount,

        onEditingRowCancel: () => setValidationErrors({}),

        onEditingRowSave: handleSaveDiscount,

        muiTableHeadCellProps: {},

        muiTableBodyRowProps: {
            hover: false,
            sx: {
                // height: '6vh',
            },
        },

        muiTableContainerProps: {
            sx: {
                overflowY: 'scroll',
                height: '30vh',
            },
        },

        muiSearchTextFieldProps: {
            sx: {
                width: '10rem',
            },
        },

        renderRowActions: ({ row, table }) => (
            <Box sx={{ display: 'flex' }}>
                <Tooltip title="Edit">
                    <IconButton onClick={() => table.setEditingRow(row)}>
                        <EditIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        ),

        renderTopToolbarCustomActions: ({ table }) => (
            <IconButton
                variant="contained"
                onClick={() => {
                    table.setCreatingRow(createRow(table, emptyObject));
                }}>
                <AddIcon style={{ color: 'green' }} />
                <span style={{ marginLeft: '10px', fontSize: '0.9rem', color: 'black' }}>
                    Add Discount
                </span>
            </IconButton>
        ),

        defaultColumn: {
            minSize: 105,
            maxSize: 150,
        },

        state: {},
    });

    return <MaterialReactTable table={table} />;
}

const validateDecimal = (value) => !!value && parseFloat(value) > 0;
const validateLabel = (value) => !!value && value.length > 0;

function validateDiscount(discount) {
    return {
        label: !validateLabel(discount.label) ? 'Required' : '',
        percent: !validateDecimal(discount.percent) ? 'Required > 0' : '',
    };
}

export default DiscountsInput;

